import { PointPureUpdateMutationInput, usePointUpdateMutation } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import usePoint from './usePoint';
import { PointUpdateResult } from './pointUpdateResult';

const usePointUpdate = () => {
  const [mutation, mutationResult] = usePointUpdateMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const { getPoint } = usePoint();

  const requestWithResult = async (input: PointPureUpdateMutationInput): Promise<PointUpdateResult> => {
    try {
      const response = await mutation({ variables: { input } });
      const result = response.data?.pointPureUpdate;

      if (result?.ok === false) {
        return { status: 'rejected', errors: result.errors?.flatMap((error) => error?.messages || []) || [] };
      }

      if (!result?.ok || !result.point) return { status: 'unknown' };

      // A failed refresh must not turn a confirmed write into a rejected one.
      try {
        await getPoint(input.pointId);
        return { status: 'saved', point: result.point };
      } catch (refreshError) {
        return { status: 'saved', point: result.point, refreshError };
      }
    } catch (error) {
      return { status: 'unknown', error };
    }
  };

  const request = async (input: PointPureUpdateMutationInput): Promise<boolean> => {
    const result = await requestWithResult(input);
    if (result.status === 'unknown' && result.error) throw result.error;
    return result.status === 'saved';
  };

  return {
    error: mutationResult.error,
    loading: mutationResult.loading,
    called: mutationResult.called,
    request,
    requestWithResult,
    readPoint: getPoint,
  };
};

export default usePointUpdate;
